import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import SavePDF from '@/assets/images/SavePDFIcon.svg'
import ToothGraphic2 from '@/assets/images/Tooth-graphic-2.svg'
import ToothGraphic3 from '@/assets/images/Tooth-graphic-3.svg'
import ToothGraphic4 from '@/assets/images/Tooth-graphic-4.svg'
import ToothGraphic5 from '@/assets/images/Tooth-graphic-5.svg'
import ToothGraphic6 from '@/assets/images/Tooth-graphic-6.svg'
import Button from '@/components/Button'
import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import Stepper from '@/components/Stepper'
import { useAppSelector } from '@/store'
import { setAllInOneRecipeOutput, setRecipeActiveStep } from '@/store/allInOne'
import {
  useGetCaseDetailsQuery,
  useGetMetadataQuery,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { setInternalEffectCeramicBrand } from '@/store/fullLayering'
import { isNullorEmpty } from '@/utils'
import { saveRecipeGeneric } from '@/utils/helper'
import {
  ALL_IN_ONE_STORED_TYPE,
  getAllInOneCeramicBrand,
  getDentinLayerAdvice,
  getFrameworkAdviceList,
  getProvidedInputTableKeys,
  hasPrepAdvice,
  selectDentinRegion,
} from '@/utils/matisseV2/allInOne'
import { getFrameworkBrand } from '@/utils/matisseV2/frameworks'
import { formatProvidedInputValue } from '@/utils/matisseV2/toLabColor'

import AestheticModelRecipe from './recipeOutput/AestheticModelRecipe'
import DentinRecipe from './recipeOutput/DentinRecipe'
import Framework from './recipeOutput/Framework'
import MaskingRecipe from './recipeOutput/MaskingRecipe'
import OpaquerRecipe from './recipeOutput/OpaquerRecipe'
import RecipeOutputPDF from './recipeOutput/RecipeOutputPDF'
import SkinEnamelRecipe from './recipeOutput/SkinEnamelRecipe'
import { ALL_IN_ONE_STEPS } from './utils/constants'

interface MetadataResponse {
  available_ceramic_brands: any
  available_capbrands: any
  available_stainingbrands: any
  [key: string]: any
}

const RecipeOutPut = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'allInOne.recipeOutput',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })
  const [tableKey, setTableKey] = useState<any>()
  const [selectedTab, setSelectedTab] = useState<number | string>(0)
  const [frameworkIndex, SetFrameworkIndex] = useState<number>(0)
  const [dentinCervicalIndex, setDentinCervicalIndex] = useState<number>(0)
  const [dentinMiddleIndex, setDentinMiddleIndex] = useState<number>(0)
  const [dentinIncisalIndex, setDentinIncisalIndex] = useState<number>(0)
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
  }
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const recipeOutput = useAppSelector(
    (state) => state.allInOne.allInOneRecipeOutput,
  )
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const dispatch = useDispatch()
  const { state }: any = location

  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const indication = recipeOutput?.provided_input?.indication
  const ceramicBrand = getAllInOneCeramicBrand(recipeOutput)
  const frameworkBrand = getFrameworkBrand(recipeOutput)
  const showPrepAdvice = hasPrepAdvice(recipeOutput)
  const showOpaquers = indication === 'PFM Crown'
  const showMasking = indication === 'Veneer on refractory'
  const showFramework =
    indication !== 'PFM Crown' && indication !== 'Veneer on refractory'

  useEffect(() => {
    if (isNullorEmpty(state, 'recipe')) {
      const { generation_id, ...output } = state.recipe ?? {}
      dispatch(
        setAllInOneRecipeOutput({
          result: output,
          generation_id,
        }),
      )
    }
  }, [])

  useEffect(() => {
    if (recipeOutput) {
      if (hasPrepAdvice(recipeOutput)) {
        setSelectedTab('Aesthetic Model')
      } else if (recipeOutput?.provided_input?.indication === 'PFM Crown') {
        setSelectedTab('Opaquers')
      } else if (
        recipeOutput?.provided_input?.indication === 'Veneer on refractory'
      ) {
        setSelectedTab('Masking')
      } else if (
        recipeOutput?.provided_input?.indication !== 'PFM Crown' &&
        recipeOutput?.provided_input?.indication !== 'Veneer on refractory'
      ) {
        setSelectedTab('Framework')
      } else {
        setSelectedTab('Dentin')
      }
      setTableKey(getProvidedInputTableKeys(recipeOutput.provided_input))
    }
  }, [recipeOutput])

  const getSelectedRecipeOutput = () => {
    if (!recipeOutput) return null

    const selectedData = { ...recipeOutput }
    const cervical = selectDentinRegion(
      getDentinLayerAdvice(recipeOutput, 'cervical'),
      dentinCervicalIndex,
    )
    const middle = selectDentinRegion(
      getDentinLayerAdvice(recipeOutput, 'middle'),
      dentinMiddleIndex,
    )
    const incisal = selectDentinRegion(
      getDentinLayerAdvice(recipeOutput, 'incisal'),
      dentinIncisalIndex,
    )

    if (cervical) selectedData.dentinlayer_advice_cervical = cervical
    if (middle) selectedData.dentinlayer_advice_middle = middle
    if (incisal) selectedData.dentinlayer_advice_incisal = incisal

    if (
      selectedData.internallayer_advice &&
      recipeOutput.provided_input?.indication !== 'PFM Crown' &&
      recipeOutput.provided_input?.indication !== 'Veneer on refractory'
    ) {
      const frameworks = getFrameworkAdviceList(recipeOutput)
      selectedData.internallayer_advice = frameworks[frameworkIndex]
    }

    return selectedData
  }

  const { data: metaData } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: MetadataResponse | undefined }
  const { data: caseDetails } = useGetCaseDetailsQuery(
    { caseId },
    { skip: !caseId },
  )

  const providedInputNames = metaData?.provided_input_names

  const theme = useTheme()
  const navigate = useNavigate()

  const tabStyles = {
    color: '#999',
    textTransform: 'none',
    '&.Mui-selected': {
      color: '#90CAF9',
    },
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: "'Inter'",
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    const filteredOutput = getSelectedRecipeOutput()
    if (!filteredOutput) return

    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: filteredOutput,
      workflow: null,
      mutationFn: saveRecipe,
      recipeType: ALL_IN_ONE_STORED_TYPE,
      closeDialog: () => setSaveRecipedialogOpen(false),
    })
  }

  const generatePdf = async () => {
    const filteredOutput = getSelectedRecipeOutput()
    if (!filteredOutput) return

    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: filteredOutput,
      workflow: null,
      mutationFn: savePdf,
      recipeType: ALL_IN_ONE_STORED_TYPE,
      isPdf: true,
    })
  }

  const handleStepClick = (index: number) => {
    if (index === 0 || index === 1) {
      navigate(`/all-in-one${location.search}`)
      dispatch(setRecipeActiveStep(index))
    }
  }

  const handleBackClick = () => {
    navigate(`/projects/${caseId}`)
  }
  return (
    <>
      {recipeOutput && (
        <Box display="flex" flexGrow={1} flexDirection="column" zIndex={1111}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={4}
            py={2}
            sx={{
              position: 'relative',
            }}
          >
            {caseDetails?.case_type !== 'auto' && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 32,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="text"
                  onClick={handleBackClick}
                  sx={{
                    color: '#90CAF9',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '26px',
                  }}
                  label={t('backToProject')}
                  startIcon={<ChevronLeftIcon />}
                  textTransform="uppercase"
                />
              </Box>
            )}

            <Box display={'flex'} flexGrow={1}>
              <Stepper
                activeStep={3}
                steps={ALL_IN_ONE_STEPS}
                onStepClick={handleStepClick}
              />
            </Box>
          </Box>

          <Box
            sx={{
              height: 'calc(100% - 80px)',
              backgroundColor: theme.palette.background.paper,
              overflowY: 'auto',
              paddingX: 6,
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                margin: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mx={2}
                mb={3}
                mt={4}
              >
                <Box sx={{ color: 'white', fontSize: 24 }}>
                  {t('yourRecipe')}
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    label={translate('saveAsPdf')}
                    variant="outlined"
                    startIcon={<img src={SavePDF} alt="savePDF" />}
                    onClick={async () => generatePdf()}
                    textTransform="uppercase"
                  />
                  <Button
                    label={translate('saveToProject')}
                    startIcon={<CloudDownloadIcon />}
                    onClick={async () => handleOpenSaveDialog()}
                    textTransform="uppercase"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 6,
                  height: 475,
                }}
              >
                {/* Image Container */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background:
                      'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                    padding: 4,
                    borderRadius: 4,
                    height: 475,
                  }}
                >
                  {(canvasImage?.calibrated_image || canvasImage?.image) && (
                    <img
                      src={canvasImage?.calibrated_image || canvasImage?.image}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        margin: 'auto',
                        height: '411px',
                      }}
                      alt="recipeimage"
                    />
                  )}
                </Box>

                {/* Table Container */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    padding: 4,
                    background:
                      'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                    borderRadius: 4,
                    height: 475,
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <tbody>
                      {tableKey?.map((key: string | number) => (
                        <tr key={String(key)}>
                          {recipeOutput?.provided_input[key] && (
                            <th style={{ padding: 8, textAlign: 'left' }}>
                              {providedInputNames?.[key]}
                            </th>
                          )}
                          {recipeOutput.provided_input[key] && (
                            <td style={{ padding: 8, textAlign: 'left' }}>
                              {formatProvidedInputValue(
                                recipeOutput.provided_input[key],
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              // my={4}
              paddingTop="32px"
              paddingBottom="24px"
            >
              <Typography
                sx={{ color: 'white', fontSize: 24, lineHeight: '20px' }}
              >
                {t('recipeDetail')}
              </Typography>
              <Button
                label={t('proceedToInternalEffect')}
                endIcon={<ChevronRightIcon />}
                onClick={() => {
                  if (ceramicBrand) {
                    dispatch(setInternalEffectCeramicBrand(ceramicBrand))
                  }
                  navigate(`/full-layering/internal-effects${location.search}`)
                }}
                textTransform="uppercase"
              />
            </Box>
            <Box
              sx={{
                margin: 'auto',
                maxHeight: 'fit-content',
                // height: 'max(100%, 561px)',
                background: 'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                borderRadius: '16px',
                padding: '32px',
              }}
            >
              {/* Content Section */}
              <Box
                display="flex"
                height="100%"
                sx={{
                  '@media (max-width: 1080px)': {
                    flexDirection: 'column',
                  },
                }}
              >
                <Box
                  flex={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  margin="auto"
                  sx={{ minWidth: '316px' }}
                >
                  {selectedTab === 'Dentin' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic4}
                      alt="Toothimage"
                    />
                  )}
                  {selectedTab === 'Skin Enamel' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic2}
                      alt="Toothimage"
                    />
                  )}
                  {selectedTab === 'Aesthetic Model' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic6}
                      alt="Toothimage"
                    />
                  )}
                  {(selectedTab === 'Opaquers' ||
                    selectedTab === 'Masking' ||
                    selectedTab === 'Framework') && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic5}
                      alt="Toothimage"
                    />
                  )}
                  {selectedTab === 'Internal Effects' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic3}
                      alt="Toothimage"
                    />
                  )}
                </Box>

                {/* Tabs Section */}
                <Box
                  flex={7}
                  sx={{
                    height: 'fit-content',
                  }}
                  display="flex"
                  flexDirection="column"
                  gap="32px"
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    sx={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    {showPrepAdvice && (
                      <Tab
                        label="Aesthetic Model"
                        value="Aesthetic Model"
                        sx={tabStyles}
                      />
                    )}

                    {showOpaquers && (
                      <Tab label="Opaquers" value="Opaquers" sx={tabStyles} />
                    )}

                    {showMasking && (
                      <Tab label="Masking" value="Masking" sx={tabStyles} />
                    )}

                    {showFramework && (
                      <Tab label="Framework" value="Framework" sx={tabStyles} />
                    )}

                    {['Dentin', 'Skin Enamel'].map((label) => (
                      <Tab
                        key={label}
                        label={label}
                        value={label}
                        sx={tabStyles}
                      />
                    ))}
                  </Tabs>

                  {/* Tab Panels */}

                  {selectedTab === 'Aesthetic Model' && showPrepAdvice && (
                    <AestheticModelRecipe
                      recipeOutput={recipeOutput}
                      natural_die_material={
                        recipeOutput.provided_input?.natural_die_material
                      }
                    />
                  )}

                  {selectedTab === 'Opaquers' && showOpaquers && (
                    <OpaquerRecipe recipeOutput={recipeOutput} />
                  )}

                  {selectedTab === 'Framework' && showFramework && (
                    <Framework
                      internallayer_advice={getFrameworkAdviceList(
                        recipeOutput,
                      )}
                      frameworkBrand={frameworkBrand}
                      frameworkIndex={frameworkIndex}
                      SetFrameworkIndex={SetFrameworkIndex}
                    />
                  )}

                  {selectedTab === 'Masking' && showMasking && (
                    <MaskingRecipe
                      recipeOutput={recipeOutput}
                      brand={ceramicBrand}
                    />
                  )}

                  {selectedTab === 'Dentin' && (
                    <DentinRecipe
                      recipeOutput={recipeOutput}
                      brand={ceramicBrand}
                      dentinCervicalIndex={dentinCervicalIndex}
                      setDentinCervicalIndex={setDentinCervicalIndex}
                      dentinMiddleIndex={dentinMiddleIndex}
                      setDentinMiddleIndex={setDentinMiddleIndex}
                      dentinIncisalIndex={dentinIncisalIndex}
                      setDentinIncisalIndex={setDentinIncisalIndex}
                    />
                  )}

                  {selectedTab === 'Skin Enamel' && (
                    <SkinEnamelRecipe
                      recipeOutput={recipeOutput}
                      brand={ceramicBrand}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {recipeOutput && (
        <RecipeOutputPDF
          recipe={recipeOutput}
          frameworkIndex={frameworkIndex}
          dentinCervicalIndex={dentinCervicalIndex}
          dentinMiddleIndex={dentinMiddleIndex}
          dentinIncisalIndex={dentinIncisalIndex}
          id="download-pdf"
        />
      )}
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </>
  )
}

export default RecipeOutPut
